import React, { useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button, Modal } from 'react-bootstrap'
import { useHistory } from 'react-router'
import { AGLContext } from '../../../contexts/AglContext'
import { useDispatch, useSelector } from 'react-redux'
import { updateSessionStatus } from '../../../redux/actions/patient'
import { filterActionsByActionType, getTotalS2kScore } from '../../../utils/helpers'
import { createAppointmentsRequest, createDocumentReference, saveS2K, saveWrapUp, setOrders } from '../../../redux/actions/wrapup'
import { exitAction, saveAssessment } from '../../../redux/actions/assessment'
import { saveCarePlan } from '../../../redux/actions/ccp'
import encounterStatusEnum from '../../../utils/encounterStatusEnum'

const SaveModal = ({ show, onClose, kind, title, bodyContent }) => {
  const history = useHistory()

  const agl = useContext(AGLContext)

  const {
    encounter,
    actions,
    s2k, 
    flare,
    flareIsNull,
    pga,
    rules,
    damage,
    damageIsNull,
    lldas, 
    assembledNotes,
    pendS2k,
    activeKey,
    subActions,
    opportunities
  } = useSelector((state) => ({
    encounter: state.patient.encounter,
    actions: state.ccp.actions,
    rules: state.ccp.rules,
    s2k: state.assessment.s2k,
    flare: state.assessment.flare,
    flareIsNull: state.assessment.flareIsNull,
    pga: state.assessment.pga,
    assembledNotes: state.ccp.assembledNote,
    damage: state.assessment.damage,
    damageIsNull: state.assessment.damageIsNull,
    lldas: state.assessment.lldas,
    pendS2k: state.assessment.pendS2k,
    assembledNote: state.ccp.assembledNote,
    activeKey: state.header.activeKey,
    subActions: state.ccp.subActions,
    opportunities: state.ccp.opportunities
  }))

  const dispatch = useDispatch()

  const closeOnEscapeKeyDown = (e) => {
    if ((e.charCode || e.keyCode) === 27) {
      onClose()
    }
  }

  useEffect(() => {
    document.body.addEventListener('keydown', closeOnEscapeKeyDown)
    return function cleanup () {
      document.body.removeEventListener('keydown', closeOnEscapeKeyDown)
    }
  }, [])

  useEffect(() => {
    if (agl.aglState.ordersDone) {
      const status = pendS2k ? encounterStatusEnum.S2K_PENDED.value : encounterStatusEnum.FINISHED.value
      onClose()
      dispatch(updateSessionStatus(status))
      dispatch(setOrders(agl.aglState.orders))
      dispatch(saveCarePlan(encounter, opportunities, rules, actions, subActions, agl.aglState.orders))
      history.push('/close/done', agl.aglState)
    }
  }, [agl.aglState.ordersDone])

  const handleClose = () => {
    const wrapupActions = [].concat(...rules.map((rule) => rule.activeActionIds))

    if (kind !== 'Exit') {

      // S2k
      if (!pendS2k) {
        const s2kScore = getTotalS2kScore(s2k)
        dispatch(saveS2K(s2kScore))
      }

      // Appointments
      const scheduling = filterActionsByActionType(rules, actions, subActions, 'Schedule')
      dispatch(createAppointmentsRequest(scheduling))

      // Notes
      dispatch(createDocumentReference(assembledNotes))
      dispatch(saveWrapUp(encounter, assembledNotes))

      const orders = filterActionsByActionType(rules, actions, subActions, 'Order')
      const rx = filterActionsByActionType(rules, actions, subActions, 'Rx')
      const orderReferrals = filterActionsByActionType(rules, actions, subActions, 'Order Referral')

      const ordersToPlace = [].concat(...orders, ...rx, ...orderReferrals)
      dispatch(setOrders(ordersToPlace))
      agl.setAglState((ps) => ({
        ...ps,
        orders: ordersToPlace,
        processOrders: true,
        orderIndex: 0
      }))
    } else {
      handleActiveKey()
      history.push('/close/exit')
    }

    dispatch(exitAction(encounter, wrapupActions, assembledNotes, activeKey))
  }

  const handleAssessment = (dispatch, encounter, pga, flare, s2k, damage, lldas, flareIsNull, damageIsNull) => {
    dispatch(saveAssessment(encounter, pga, flare, s2k, damage, lldas, flareIsNull, damageIsNull))
    dispatch(updateSessionStatus(encounterStatusEnum.DRAFT_CREATED.value))
  }
  
  const handleCarePlan = (dispatch) => {
    dispatch(saveCarePlan())
    dispatch(updateSessionStatus(encounterStatusEnum.DRAFT_RECC.value))
  }
  
  const handleWrapUp = (dispatch) => {
    dispatch(updateSessionStatus(encounterStatusEnum.DRAFT_REVIEW.value))
  }

  const handleActiveKey = () => {
    switch (activeKey) {
    case 'assessment':
      handleAssessment(dispatch, encounter, pga, flare, s2k, damage, lldas, flareIsNull, damageIsNull)
      break
    case 'ccp':
      handleCarePlan(dispatch)
      break
    case 'wrapup':
      handleWrapUp(dispatch)
      break
    default:
      break
    }
  }

  return (
    <Modal
      id='save-modal'
      size='lg'
      show={show}
      onHide={onClose}
    >
      <Modal.Header>
        <Modal.Title className='h4'>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {bodyContent}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='outline-primary'
          className='mr-1'
          onClick={onClose}
        >
            Cancel
        </Button>
        <Button
          variant='primary'
          onClick={() => handleClose()}
        >
          {kind}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

SaveModal.propTypes = {
  show: PropTypes.bool,
  kind: PropTypes.string,
  onClose: PropTypes.func,
  title: PropTypes.string,
  bodyContent: PropTypes.string
}
  

export default SaveModal